/**
 * Narrows full procedure snapshot trees to the audited node (section / step / substep / child),
 * matching RevisionPreview so Review route shows the same focused subtree for View Original / View New.
 */

import { sortSectionsForSnapshotDisplay } from "./sectionDisplayOrdinal";

/**
 * Must match `MoveAction/RevisionPreview/Step/Step.js` (substep A/B/C and grey path "Substep 3B").
 * When we narrow a snapshot to a single substep, sibling substeps are removed from the array —
 * we still attach the original 0-based letter index for labelling.
 */
function getSubstepSortKeyNarrow(ss) {
  const d = ss?.detail || {};
  const vc = ss?.version_details?.version_content || {};
  const raw =
    ss?.sibling_order ??
    ss?.procedure_tree?.sibling_order ??
    d.sibling_order ??
    vc.sibling_order ??
    "0";
  const n = parseInt(raw, 10);
  return Number.isNaN(n) ? 0 : n;
}

function getSubstepStableIdNarrow(ss) {
  return (
    ss?.tree_node_id ||
    ss?.detail?.sub_step_tree_node_id ||
    ss?.version_details?.version_content?.sub_step_tree_node_id ||
    ss?.sub_step_tree_node_id ||
    ss?.substep_tree_node_id ||
    ss?.substep_id_ref ||
    ""
  );
}

function sortedSubstepsForLabelRankNarrow(substeps) {
  if (!Array.isArray(substeps) || substeps.length < 2) return substeps || [];
  return [...substeps].sort((a, b) => {
    const oa = getSubstepSortKeyNarrow(a);
    const ob = getSubstepSortKeyNarrow(b);
    if (oa !== ob) return oa - ob;
    return substeps.indexOf(a) - substeps.indexOf(b);
  });
}

function getSubstepLetterIndexAmongSiblings(allSubsteps, targetSubstep) {
  if (!targetSubstep) return 0;
  const sorted = sortedSubstepsForLabelRankNarrow(
    Array.isArray(allSubsteps) ? allSubsteps : [],
  );
  if (sorted.length === 0) return 0;
  const id = getSubstepStableIdNarrow(targetSubstep);
  const idx = sorted.findIndex(
    (s) =>
      s === targetSubstep ||
      (id && getSubstepStableIdNarrow(s) === id) ||
      (!id && s === targetSubstep),
  );
  return idx >= 0 ? idx : 0;
}

export function getAuditNarrowHint(revData) {
  const vc = revData?.version_content || {};
  return `${vc.action_to_show || ""} ${revData?.action || ""}`;
}

function pickFirstDefined(...values) {
  for (const v of values) {
    if (v != null && v !== "") return v;
  }
  return null;
}

/** Same predicate as RevisionPreview.allowNarrowToSingleNodeSnapshot (section/step/substep edits, etc.). */
export function auditAllowNarrowToSingleNodeSnapshot(narrowHint) {
  const h = narrowHint || "";
  return (
    /SAVE\s+SECTION|CREATE_SECTION|UPDATE_SECTION|EDIT\s+SECTION/i.test(h) ||
    /SAVE\s+STEP|CREATE_STEP|UPDATE_STEP|EDIT\s+STEP/i.test(h) ||
    /SAVE\s+SUBSTEP|CREATE_SUBSTEP|UPDATE_SUBSTEP|EDIT\s+SUBSTEP/i.test(h) ||
    /SAVE\s+ATTACHMENT|CREATE_ATTACHMENT|UPDATE_ATTACHMENT|EDIT\s+ATTACHMENT/i.test(
      h,
    ) ||
    /SAVE\s+CANVAS|CREATE_CANVAS|UPDATE_CANVAS|EDIT\s+CANVAS/i.test(h) ||
    /SAVE\s+RICH\s+TEXT|CREATE_RICH_TEXT|UPDATE_RICH_TEXT|EDIT\s+RICH\s+TEXT/i.test(
      h,
    ) ||
    /SAVE\s+VIDEO|CREATE_VIDEO|UPDATE_VIDEO|EDIT\s+VIDEO/i.test(h) ||
    /VIDEO_(?:MOVED|COPIED)/i.test(h) ||
    /(?:CANVAS|CANVA)_(?:MOVED|COPIED)/i.test(h) ||
    /ATTACHMENT_(?:MOVED|COPIED)/i.test(h) ||
    /(?:RICH_TEXT|RICH-TEXT)_(?:MOVED|COPIED)/i.test(h) ||
    /SECTION_(?:MOVED|COPIED|DELETED)/i.test(h) ||
    /\bUser\s+(?:Moved|Copied|Deleted)\s+Section\s+\d+/i.test(h) ||
    /\b(?:Copy|Move|Delete)\s+Section\s+\d+/i.test(h)
  );
}

export function resolveSnapshotTreeNodeIdForAudit(
  revData,
  selectedView,
  previousMeta,
  newMeta,
) {
  const vc = revData?.version_content || {};
  const pvc = revData?.parent_version_data?.version_content || {};

  const fromVc = () =>
    pickFirstDefined(
      vc.preview_tree_node_id,
      vc.procedure_tree_ref,
      vc.tree_node_id,
      vc.step_tree_node_id,
      vc.sub_step_tree_node_id,
      vc.substep_tree_node_id,
      vc.section_tree_node_id,
    );

  const fromPvc = () =>
    pickFirstDefined(
      pvc.preview_tree_node_id,
      pvc.procedure_tree_ref,
      pvc.tree_node_id,
      pvc.step_tree_node_id,
      pvc.sub_step_tree_node_id,
      pvc.section_tree_node_id,
    );

  if (selectedView === "original") {
    return pickFirstDefined(
      previousMeta?.tree_node_id,
      fromVc(),
      fromPvc(),
    );
  }
  return pickFirstDefined(
    newMeta?.tree_node_id,
    previousMeta?.tree_node_id,
    fromVc(),
    fromPvc(),
  );
}

export function resolveSnapshotNodeTypeForAudit(
  revData,
  selectedView,
  previousMeta,
  newMeta,
) {
  const snapshotMetaForFilter =
    selectedView === "original" ? previousMeta : newMeta;
  const vc = revData?.version_content || {};
  const pvc = revData?.parent_version_data?.version_content || {};
  const raw =
    selectedView === "original"
      ? snapshotMetaForFilter?.type || vc.type || pvc.type
      : snapshotMetaForFilter?.type ||
        previousMeta?.type ||
        vc.type ||
        pvc.type;
  const normalized =
    raw === "canva"
      ? "canvas"
      : raw === "rich_text"
        ? "rich-text"
        : raw;
  const hint = getAuditNarrowHint(revData);
  if (
    !normalized &&
    /UPDATE_SECTION|EDIT\s+SECTION|SAVE\s+SECTION|CREATE_SECTION/i.test(hint)
  ) {
    return "section";
  }
  if (!normalized && /SECTION_(?:MOVED|COPIED|DELETED)/i.test(hint)) {
    return "section";
  }
  if (
    !normalized &&
    /UPDATE_STEP|EDIT\s+STEP|SAVE\s+STEP|CREATE_STEP/i.test(hint)
  ) {
    return "step";
  }
  if (
    !normalized &&
    /UPDATE_SUBSTEP|EDIT\s+SUBSTEP|SAVE\s+SUBSTEP|CREATE_SUBSTEP/i.test(hint)
  ) {
    return "substep";
  }
  return normalized;
}

/**
 * @param {object} [options]
 * @param {boolean} [options.preserveSectionChromeForStepFocus] - When true (e.g. bulk_audit_trail copy),
 *   narrowing to a step still shows the parent section header, name, and description. Default hides section
 *   chrome so only the step is prominent (single-step save/edit flows).
 * @returns {Array|null} narrowed sections, or null if nothing matched
 */
export function narrowProcedureTreeBySnapshotId(
  sections,
  tid,
  snapshotNodeType,
  options = {},
) {
  if (!tid || !Array.isArray(sections)) return null;

  const preserveSectionChrome = options.preserveSectionChromeForStepFocus === true;
  /** Step-focus branches normally hide the section row; bulk copy needs section context. */
  const hideSectionHeaderForStepFocus = preserveSectionChrome ? false : true;

  const childMatchesSnapshotFocus = (ch) => {
    if (!ch || !tid) return false;
    if (ch.tree_node_id === tid) return true;
    const d = ch.detail || {};
    if (d.procedure_tree_ref === tid) return true;
    if (d.attachment_tree_node_id === tid) return true;
    if (d.canvas_tree_node_id === tid || d.canva_tree_node_id === tid)
      return true;
    if (d.rich_text_node_id === tid || d.rich_text_tree_node_id === tid)
      return true;
    if (d.video_tree_node_id === tid) return true;
    if (
      Array.isArray(d.attachments) &&
      d.attachments.some(
        (a) =>
          a?.attachment_tree_node_ref === tid || a?.attachment_id === tid,
      )
    ) {
      return true;
    }
    return false;
  };

  if (snapshotNodeType === "section") {
    const bySec = sections.filter((s) => s && s.tree_node_id === tid);
    return bySec.length > 0 ? bySec : null;
  }
  if (snapshotNodeType === "step") {
    const stepMatchesFocus = (st) => {
      if (!st) return false;
      if (st.tree_node_id === tid) return true;
      const d = st.detail || {};
      if (d.procedure_tree_ref === tid) return true;
      if (d.step_tree_node_id === tid) return true;
      return false;
    };
    for (const sec of sections) {
      if (!sec) continue;
      const steps = (sec.steps || []).filter(stepMatchesFocus);
      if (steps.length === 0) continue;
      /* Step audit: usually hide parent section row (title already names the step); bulk keeps section chrome. */
      return [{ ...sec, hideSectionHeader: hideSectionHeaderForStepFocus, steps }];
    }
  }
  if (snapshotNodeType === "substep") {
    const substepMatchesFocus = (ss) => {
      if (!ss) return false;
      if (ss.tree_node_id === tid) return true;
      const d = ss.detail || {};
      if (d.procedure_tree_ref === tid) return true;
      if (d.sub_step_tree_node_id === tid) return true;
      return false;
    };
    for (const sec of sections) {
      if (!sec) continue;
      for (const st of sec.steps || []) {
        if (!st) continue;
        const allSs = st.substeps || [];
        const substeps = allSs.filter(substepMatchesFocus).map((ss) => ({
          ...ss,
          auditPreviewSubstepLetterIndex: getSubstepLetterIndexAmongSiblings(
            allSs,
            ss,
          ),
        }));
        if (substeps.length === 0) continue;
        return [
          {
            ...sec,
            hideSectionHeader: true,
            steps: [
              {
                ...st,
                hideStepHeader: true,
                children: [],
                substeps,
              },
            ],
          },
        ];
      }
    }
  }
  if (
    snapshotNodeType === "attachment" ||
    snapshotNodeType === "canvas" ||
    snapshotNodeType === "rich-text" ||
    snapshotNodeType === "video"
  ) {
    for (const sec of sections) {
      if (!sec) continue;
      for (const st of sec.steps || []) {
        if (!st) continue;
        for (const ss of st.substeps || []) {
          if (!ss) continue;
          const subKids = ss.children || [];
          for (let ci = subKids.length - 1; ci >= 0; ci--) {
            const ch = subKids[ci];
            if (childMatchesSnapshotFocus(ch)) {
              const letterIdx = getSubstepLetterIndexAmongSiblings(
                st.substeps || [],
                ss,
              );
              return [
                {
                  ...sec,
                  hideSectionHeader: true,
                  steps: [
                    {
                      ...st,
                      hideStepHeader: true,
                      children: [],
                      substeps: [
                        {
                          ...ss,
                          hideSubstepHeader: true,
                          children: [ch],
                          auditPreviewSubstepLetterIndex: letterIdx,
                        },
                      ],
                    },
                  ],
                },
              ];
            }
          }
        }
        const stepKids = st.children || [];
        for (let ci = stepKids.length - 1; ci >= 0; ci--) {
          const ch = stepKids[ci];
          if (childMatchesSnapshotFocus(ch)) {
            return [
              {
                ...sec,
                hideSectionHeader: true,
                steps: [
                  {
                    ...st,
                    hideStepHeader: true,
                    children: [ch],
                    substeps: [],
                  },
                ],
              },
            ];
          }
        }
      }
    }
  }

  const bySec = sections.filter((s) => s && s.tree_node_id === tid);
  if (bySec.length > 0) return bySec;
  for (const sec of sections) {
    if (!sec) continue;
    for (const st of sec.steps || []) {
      if (st && st.tree_node_id === tid) {
        return [
          {
            ...sec,
            hideSectionHeader: hideSectionHeaderForStepFocus,
            steps: [st],
          },
        ];
      }
    }
  }
  for (const sec of sections) {
    if (!sec) continue;
    for (const st of sec.steps || []) {
      if (!st) continue;
      for (const ss of st.substeps || []) {
        if (ss && ss.tree_node_id === tid) {
          return [
            {
              ...sec,
              hideSectionHeader: true,
              steps: [
                {
                  ...st,
                  hideStepHeader: true,
                  children: [],
                  substeps: [
                    {
                      ...ss,
                      auditPreviewSubstepLetterIndex:
                        getSubstepLetterIndexAmongSiblings(
                          st.substeps || [],
                          ss,
                        ),
                    },
                  ],
                },
              ],
            },
          ];
        }
      }
    }
  }
  for (const sec of sections) {
    if (!sec) continue;
    for (const st of sec.steps || []) {
      if (!st) continue;
      for (const ch of st.children || []) {
        if (childMatchesSnapshotFocus(ch)) {
          return [
            {
              ...sec,
              hideSectionHeader: true,
              steps: [
                {
                  ...st,
                  hideStepHeader: true,
                  children: [ch],
                  substeps: [],
                },
              ],
            },
          ];
        }
      }
      for (const ss of st.substeps || []) {
        if (!ss) continue;
        for (const ch of ss.children || []) {
          if (childMatchesSnapshotFocus(ch)) {
            const letterIdx = getSubstepLetterIndexAmongSiblings(
              st.substeps || [],
              ss,
            );
            return [
              {
                ...sec,
                hideSectionHeader: true,
                steps: [
                  {
                    ...st,
                    hideStepHeader: true,
                    children: [],
                    substeps: [
                      {
                        ...ss,
                        hideSubstepHeader: true,
                        children: [ch],
                        auditPreviewSubstepLetterIndex: letterIdx,
                      },
                    ],
                  },
                ],
              },
            ];
          }
        }
      }
    }
  }
  return null;
}

/**
 * /audit-trail/review/:id — show the full JSON from previous_content_snapshot (Original) or
 * new_content_snapshot (New) without trimming rows. Section order is normalized for display.
 */
export function narrowReviewPreviewContent(
  processedSections,
  _revData,
  _selectedView,
  _previousMeta,
  _newMeta,
) {
  if (!Array.isArray(processedSections) || processedSections.length === 0) {
    return processedSections;
  }
  return sortSectionsForSnapshotDisplay(processedSections);
}
